import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiGrid,
  FiList,
  FiSearch,
  FiFileText,
  FiCopy,
  FiDollarSign,
  FiClock,
  FiPackage,
  FiBarChart2,
  FiColumns,
  FiPrinter
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import DownloadReportModal from '../../components/UI/Modal/DownloadReportModal';
import { formulacionesService, reportesService } from '../../services';
import { useDebounce } from '../../hooks/useDebounce';

const Formulaciones = () => {
  const navigate = useNavigate();
  const [formulaciones, setFormulaciones] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [viewMode, setViewMode] = useState('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('todas');
  const [error, setError] = useState(null);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  // Aplicar debounce al searchQuery
  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  useEffect(() => {
    loadFormulaciones();
  }, [activeTab]);

  useEffect(() => {
    performSearch(debouncedSearchQuery);
  }, [debouncedSearchQuery]);

  const transformFormulacionFromAPI = useCallback((formula) => ({
    id: formula.id,
    nombreProducto: formula.formula_name || '',
    compania: formula.company_name || '',
    tipoFormula: formula.formula_type || 'betel',
    tipoEnvase: formula.container_type || '',
    cantidadPorEnvase: formula.quantity_per_container || 0,
    unidad: formula.quantity_unit || 'UNIT',
    tipoCapsulas: formula.capsule_type || '',
    tiempoMezcla: formula.mixing_time_minutes || 0,
    fechaMejorUso: formula.best_use_date_years || 0,
    incluyeSilica: formula.includes_silica || false,
    costoSilica: formula.silica_cost || 0,
    ingredientes: formula.ingredients || [],
    cantidadIngredientes: formula.total_ingredients || (formula.ingredients || []).length,
    notas: formula.notes || '',
    fechaCreacion: formula.created_at || '',
    fechaActualizacion: formula.updated_at || '',
    estado: formula.is_active ? 'activa' : 'inactiva'
  }), []);

  const loadFormulaciones = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const baseParams = {
        skip: 0,
        limit: 100,
        is_active: activeTab !== 'inactivas'
      };

      // Filtrar por tipo de fórmula según la pestaña
      if (activeTab === 'betel') {
        baseParams.formula_type = 'betel';
      } else if (activeTab === 'naturalandlab') {
        baseParams.formula_type = 'naturalandlab';
      } else if (activeTab === 'exclusivas') {
        baseParams.company_name = '';  // Filtrar por compañía diferente de betel
      }

      const result = await formulacionesService.getFormulaciones(baseParams);

      if (result.success) {
        const formulacionesTransformadas = result.data.map(transformFormulacionFromAPI);
        setFormulaciones(formulacionesTransformadas);
      } else {
        setError(result.message || 'Error al cargar formulaciones');
        setFormulaciones([]);
      }
    } catch (error) {
      setError('Error de conexión al cargar formulaciones');
      console.error('Error al cargar formulaciones:', error);
      setFormulaciones([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, transformFormulacionFromAPI]);

  const performSearch = useCallback(async (query) => {
    if (!query.trim()) {
      loadFormulaciones();
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const searchParams = {
        skip: 0,
        limit: 100,
        is_active: activeTab !== 'inactivas',
        search: query
      };

      const result = await formulacionesService.getFormulaciones(searchParams);

      if (result.success) {
        const formulacionesData = result.data.map(transformFormulacionFromAPI);
        setFormulaciones(formulacionesData);
      } else {
        setError(result.message || 'Error en la búsqueda');
        setFormulaciones([]);
      }
    } catch (err) {
      setError('Error de conexión en la búsqueda');
      console.error('Error searching formulaciones:', err);
      setFormulaciones([]);
    } finally {
      setIsLoading(false);
    }
  }, [activeTab, transformFormulacionFromAPI, loadFormulaciones]);

  const handleDownload = () => {
    setShowDownloadModal(true);
  };

  const handleDownloadReport = async ({ startDate, endDate, format }) => {
    setIsDownloading(true);

    try {
      // Llamar al servicio de reportes
      const params = {
        format: format, // csv o pdf
        date_from: startDate,
        date_to: endDate,
        is_active: activeTab !== 'inactivas'
      };

      // Agregar filtro por tipo de fórmula si aplica
      if (activeTab === 'betel') {
        params.formula_type = 'betel';
      } else if (activeTab === 'naturalandlab') {
        params.formula_type = 'naturalandlab';
      }

      const result = await reportesService.exportarFormulaciones(params);

      if (result.success) {
        // Determinar el tipo MIME según el formato
        const mimeTypes = {
          csv: 'text/csv',
          pdf: 'application/pdf',
          excel: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
        };

        const mimeType = mimeTypes[format] || 'application/octet-stream';

        // Crear blob y descargar
        const blob = new Blob([result.data], { type: mimeType });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;

        // Nombre del archivo con fecha
        const fileName = `formulaciones_${new Date().toISOString().split('T')[0]}.${format}`;
        link.setAttribute('download', fileName);

        document.body.appendChild(link);
        link.click();
        link.remove();

        // Limpiar URL
        window.URL.revokeObjectURL(url);

        setShowDownloadModal(false);
      } else {
        alert('Error al generar el reporte: ' + (result.message || 'Error desconocido'));
      }
    } catch (error) {
      console.error('Error al descargar reporte:', error);
      alert('Error al descargar el reporte: ' + error.message);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleClearSearch = () => {
    setSearchQuery('');
  };

  const handleClearError = () => {
    setError(null);
    loadFormulaciones();
  };

  const handleDuplicar = async (formulacion) => {
    try {
      const result = await formulacionesService.duplicarFormulacion(formulacion.id, {
        new_company_name: formulacion.compania,
        new_formula_name: `${formulacion.nombreProducto} (Copia)`,
        copy_ingredients: true,
        copy_production_quantities: true
      });

      if (result.success) {
        navigate(`/system/formulaciones/${result.data.id}`);
      }
    } catch (error) {
      console.error('Error al duplicar formulación:', error);
    }
  };

  const handleImprimir = async (formulacion) => {
    try {
      console.log('Imprimiendo formulación:', formulacion);

      // Obtener los detalles completos de la fórmula
      const detalleResult = await formulacionesService.getFormulacion(formulacion.id);
      console.log('Resultado detalle:', detalleResult);

      if (!detalleResult.success) {
        console.error('Error al cargar detalles de la fórmula:', detalleResult);
        alert('Error al cargar los detalles de la fórmula');
        return;
      }

      const formulaDetalle = detalleResult.data;
      console.log('Formula detalle cargada:', formulaDetalle);

      // Generar el cálculo de estimado
      const calculoResult = await formulacionesService.calcularCostos(formulacion.id, {
        production_quantity: formulaDetalle.quantity_per_container || 100
      });
      console.log('Resultado cálculo:', calculoResult);

      if (!calculoResult.success) {
        console.error('Error al calcular costos:', calculoResult);
        alert('Error al calcular los costos de la fórmula');
        return;
      }

      const calculo = calculoResult.data;
      console.log('Calculo obtenido:', calculo);

      generarTirillaFormula(formulaDetalle, calculo);
    } catch (error) {
      console.error('Error al generar impresión:', error);
      alert('Error al generar la impresión: ' + error.message);
    }
  };

  const generarTirillaFormula = (formula, calculo) => {
    const tirillaHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="UTF-8">
        <title>Fórmula - ${formula.formula_name || 'Sin nombre'}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }

          body {
            font-family: 'Courier New', monospace;
            padding: 20px;
            max-width: 400px;
            margin: 0 auto;
            font-size: 12px;
            line-height: 1.4;
          }

          .header {
            text-align: center;
            margin-bottom: 20px;
            border-bottom: 2px dashed #333;
            padding-bottom: 15px;
          }

          .logo {
            font-size: 18px;
            font-weight: bold;
            margin-bottom: 5px;
            color: #7B2D8E;
          }

          .title {
            font-size: 14px;
            font-weight: bold;
            margin: 10px 0;
            text-transform: uppercase;
          }

          .info-section {
            margin-bottom: 15px;
            padding-bottom: 10px;
            border-bottom: 1px dashed #999;
          }

          .info-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 5px;
          }

          .label {
            font-weight: bold;
          }

          .value {
            text-align: right;
          }

          .ingredients-table {
            width: 100%;
            margin: 15px 0;
          }

          .ingredients-table th {
            text-align: left;
            padding: 8px 0;
            border-bottom: 2px solid #333;
            font-weight: bold;
          }

          .ingredients-table td {
            padding: 5px 0;
            border-bottom: 1px dotted #999;
          }

          .text-right {
            text-align: right;
          }

          .text-center {
            text-align: center;
          }

          .totals {
            margin-top: 15px;
            padding-top: 10px;
            border-top: 2px solid #333;
          }

          .total-row {
            display: flex;
            justify-content: space-between;
            margin-bottom: 8px;
            font-size: 13px;
          }

          .total-row.grand-total {
            font-size: 16px;
            font-weight: bold;
            margin-top: 10px;
            padding-top: 10px;
            border-top: 2px solid #333;
          }

          .footer {
            margin-top: 20px;
            padding-top: 15px;
            border-top: 2px dashed #333;
            text-align: center;
            font-size: 11px;
            color: #666;
          }

          @media print {
            body {
              padding: 10px;
            }
          }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">BETEL NATURAL</div>
          <div class="title">FÓRMULA - ${formula.formula_name || 'Sin nombre'}</div>
          <div>${formula.company_name || ''}</div>
          <div style="margin-top: 10px; font-size: 11px;">
            Fecha: ${new Date().toLocaleDateString('es-MX', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </div>
        </div>

        <div class="info-section">
          <div class="info-row">
            <span class="label">Tipo de Fórmula:</span>
            <span class="value">${translateFormulaType(formula.formula_type)}</span>
          </div>
          <div class="info-row">
            <span class="label">Tipo de Envase:</span>
            <span class="value">${translateContainerType(formula.container_type)}</span>
          </div>
          <div class="info-row">
            <span class="label">Cantidad por Envase:</span>
            <span class="value">${formula.quantity_per_container || 0} ${translateUnit(formula.quantity_unit)}</span>
          </div>
          ${formula.capsule_type ? `
          <div class="info-row">
            <span class="label">Tipo de Cápsula:</span>
            <span class="value">${translateCapsuleType(formula.capsule_type)}</span>
          </div>
          ` : ''}
        </div>

        <table class="ingredients-table">
          <thead>
            <tr>
              <th>Ingrediente</th>
              <th class="text-right">Cant.</th>
              <th class="text-right">Costo Unit.</th>
              <th class="text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            ${(calculo.ingredient_costs || []).map(ing => `
              <tr>
                <td>${ing.ingredient_name}</td>
                <td class="text-right">${parseFloat(ing.total_quantity || 0).toFixed(2)} ${ing.unit}</td>
                <td class="text-right">$${parseFloat(ing.unit_cost || 0).toFixed(2)}</td>
                <td class="text-right">$${parseFloat(ing.total_cost || 0).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <div class="totals">
          <div class="total-row">
            <span>Subtotal Ingredientes:</span>
            <span>$${parseFloat(calculo.total_ingredient_cost || 0).toFixed(2)}</span>
          </div>
          ${parseFloat(calculo.packaging_cost || 0) > 0 ? `
          <div class="total-row">
            <span>Empaque/Sílica:</span>
            <span>$${parseFloat(calculo.packaging_cost || 0).toFixed(2)}</span>
          </div>
          ` : ''}
          ${parseFloat(calculo.labor_cost || 0) > 0 ? `
          <div class="total-row">
            <span>Mano de Obra:</span>
            <span>$${parseFloat(calculo.labor_cost || 0).toFixed(2)}</span>
          </div>
          ` : ''}
          ${parseFloat(calculo.overhead_cost || 0) > 0 ? `
          <div class="total-row">
            <span>Gastos Generales:</span>
            <span>$${parseFloat(calculo.overhead_cost || 0).toFixed(2)}</span>
          </div>
          ` : ''}
          <div class="total-row grand-total">
            <span>COSTO TOTAL:</span>
            <span>$${parseFloat(calculo.total_cost || 0).toFixed(2)}</span>
          </div>
          <div class="total-row">
            <span>Costo por Unidad:</span>
            <span>$${parseFloat(calculo.cost_per_unit || 0).toFixed(2)}</span>
          </div>
        </div>

        ${formula.notes ? `
        <div class="info-section" style="margin-top: 20px;">
          <div class="label" style="margin-bottom: 5px;">Notas:</div>
          <div>${formula.notes}</div>
        </div>
        ` : ''}

        <div class="footer">
          <div>Generado con Betel Natural</div>
          <div style="margin-top: 5px;">Este documento es solo para uso interno</div>
        </div>

        <script>
          window.onload = function() {
            window.print();
          };
        </script>
      </body>
      </html>
    `;

    const ventanaImpresion = window.open('', '_blank', 'width=400,height=600');
    ventanaImpresion.document.write(tirillaHTML);
    ventanaImpresion.document.close();
  };

  const translateFormulaType = (type) => {
    const tipos = {
      'betel': 'Betel Natural',
      'naturalandlab': 'Natural & Lab',
      'healthyliving': 'Healthy Living'
    };
    return tipos[type] || type;
  };

  const translateContainerType = (type) => {
    const tipos = {
      'CAPSULES': 'CÁPSULAS',
      'POWDER': 'POLVO',
      'LIQUID': 'LÍQUIDO',
      'TABLET': 'TABLETA',
      'CREAM': 'CREMA',
      'OIL': 'ACEITE'
    };
    return tipos[type] || type;
  };

  const translateCapsuleType = (type) => {
    const tipos = {
      'SIZE_00': 'Tamaño 00',
      'SIZE_0': 'Tamaño 0',
      'SIZE_1': 'Tamaño 1',
      'SIZE_2': 'Tamaño 2',
      'SIZE_3': 'Tamaño 3',
      'VEGGIE': 'Vegetariana',
      'GELATIN': 'Gelatina'
    };
    return tipos[type] || type;
  };

  const translateUnit = (unit) => {
    const unidades = {
      'MG': 'mg',
      'G': 'g',
      'KG': 'kg',
      'ML': 'ml',
      'L': 'l',
      'UNIT': 'unidad',
      'UNITS': 'unidades'
    };
    return unidades[unit] || unit;
  };

  // Calcular totales por pestaña
  const getTotalFormulaciones = useCallback((tipo) => {
    return formulaciones.filter(f => {
      if (tipo === 'todas') return true;
      if (tipo === 'betel') return f.tipoFormula === 'betel';
      if (tipo === 'naturalandlab') return f.tipoFormula === 'naturalandlab';
      if (tipo === 'exclusivas') return f.tipoFormula !== 'betel' && f.tipoFormula !== 'naturalandlab';
      if (tipo === 'inactivas') return f.estado === 'inactiva';
      return true;
    }).length;
  }, [formulaciones]);

  const getThumbnailType = (tipoFormula) => {
    switch (tipoFormula) {
      case 'betel':
        return 'blue-dark-gradient';
      case 'naturalandlab':
        return 'green-gradient';
      default:
        return 'purple-gradient';
    }
  };

  const getStatusBadgeClass = (estado) => {
    switch (estado) {
      case 'activa':
        return 'status-badge status-badge--success';
      case 'inactiva':
        return 'status-badge status-badge--danger';
      default:
        return 'status-badge';
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(amount);
  };

  if (isLoading) {
    return <LoadingGrid message="Cargando formulaciones..." />;
  }

  return (
    <div className="formulaciones-page">
      {/* Error message */}
      {error && (
        <div className="alert alert--danger">
          <FiFileText className="alert-icon" />
          <div className="alert-content">
            <strong>Error:</strong> {error}
            <button
              className="btn btn--ghost btn--sm"
              onClick={handleClearError}
              style={{ marginLeft: '10px' }}
            >
              Reintentar
            </button>
          </div>
        </div>
      )}

      {/* Título */}
      <h1 className="page-title">FORMULACIONES</h1>

      {/* Menú de acciones */}
      <div className="page-header-with-menu">
        <div className="action-menu">
          <div className="menu-item menu-item--search">
            <label className="search-label">BUSCAR:</label>
            <input
              type="text"
              className="search-input"
              placeholder="NOMBRE FÓRMULA"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>

          <button className="menu-item menu-item--download" onClick={handleDownload}>
            DESCARGAR LISTA ↓
          </button>

          <div className="menu-item menu-item--views">
            <button
              className={`view-btn ${viewMode === 'grid' ? 'active' : ''}`}
              onClick={() => setViewMode('grid')}
              title="Vista de tarjetas"
            >
              <FiGrid />
            </button>
            <button
              className={`view-btn ${viewMode === 'list' ? 'active' : ''}`}
              onClick={() => setViewMode('list')}
              title="Vista de lista"
            >
              <FiList />
            </button>
            <button
              className={`view-btn ${viewMode === 'table' ? 'active' : ''}`}
              onClick={() => setViewMode('table')}
              title="Vista de tabla"
            >
              <FiColumns />
            </button>
          </div>
        </div>
      </div>

      {/* Filter tabs */}
      <div className="filter-tabs-container">
        <div className="filter-tabs">
          <button
            className={`filter-tab ${activeTab === 'todas' ? 'active' : ''}`}
            onClick={() => setActiveTab('todas')}
          >
            <span className="tab-label">Todas</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('todas')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'betel' ? 'active' : ''}`}
            onClick={() => setActiveTab('betel')}
          >
            <span className="tab-label">Betel Natural</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('betel')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'naturalandlab' ? 'active' : ''}`}
            onClick={() => setActiveTab('naturalandlab')}
          >
            <span className="tab-label">Natural & Lab</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('naturalandlab')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'exclusivas' ? 'active' : ''}`}
            onClick={() => setActiveTab('exclusivas')}
          >
            <span className="tab-label">Exclusivas</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('exclusivas')}</span>
          </button>
          <button
            className={`filter-tab ${activeTab === 'inactivas' ? 'active' : ''}`}
            onClick={() => setActiveTab('inactivas')}
          >
            <span className="tab-label">Inactivas</span>{' '}
            <span className="tab-count">{getTotalFormulaciones('inactivas')}</span>
          </button>
        </div>
      </div>

      {/* Contenido */}
      {formulaciones.length === 0 && !isLoading && !error ? (
        <div className="empty-state">
          <div className="empty-state-icon">
            <FiFileText size={48} />
          </div>
          <h3 className="empty-state-title">
            {searchQuery ? 'No se encontraron formulaciones' : 'No hay formulaciones registradas'}
          </h3>
          <p className="empty-state-description">
            {searchQuery
              ? `No se encontraron formulaciones que coincidan con "${searchQuery}"`
              : 'Comienza agregando tu primera formulación al sistema'
            }
          </p>
          {searchQuery && (
            <p>
              <button
                className="btn btn--ghost"
                onClick={handleClearSearch}
              >
                Limpiar búsqueda
              </button>
            </p>
          )}
        </div>
      ) : viewMode === 'table' ? null : (
        <div className={viewMode === 'grid' ? 'content-grid' : 'content-list'}>
          {formulaciones.map((formulacion) => (
            <div
              key={formulacion.id}
              className={viewMode === 'grid' ? 'content-card' : 'content-card-list'}
              onClick={() => navigate(`/system/formulaciones/${formulacion.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {viewMode === 'grid' ? (
                <>
                  <div className={`content-thumbnail ${getThumbnailType(formulacion.tipoFormula)}`}>
                    <div className={getStatusBadgeClass(formulacion.estado)}>
                      {formulacion.estado}
                    </div>
                  </div>
                  <div className="content-info">
                    <div className="content-title">{formulacion.nombreProducto}</div>
                    <div className="content-subtitle">{formulacion.compania}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiPackage size={12} /> {formulacion.cantidadPorEnvase} {formulacion.unidad.toLowerCase()}
                      </span>
                      <span className="meta-item">
                        <FiClock size={12} /> {formulacion.tiempoMezcla} min
                      </span>
                    </div>
                  </div>
                </>
              ) : viewMode === 'list' ? (
                <>
                  <div className={`content-thumbnail-small ${getThumbnailType(formulacion.tipoFormula)}`}>
                  </div>
                  <div className="content-info-list">
                    <div className="content-title">{formulacion.nombreProducto}</div>
                    <div className="content-subtitle">{formulacion.compania}</div>
                    <div className="content-meta">
                      <span className="meta-item">
                        <FiPackage size={12} /> {formulacion.tipoEnvase}
                      </span>
                      <span className="meta-item">
                        {formulacion.cantidadIngredientes} ingredientes
                      </span>
                      <span className="meta-item">
                        <FiClock size={12} /> {formulacion.tiempoMezcla} min mezcla
                      </span>
                      <span className={`meta-item ${formulacion.estado === 'activa' ? '' : 'warning'}`}>
                        {formulacion.estado}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                null
              )}
            </div>
          ))}
        </div>
      )}

      {/* Vista de tabla */}
      {viewMode === 'table' && formulaciones.length > 0 && (
        <div className="formulas-list-container" style={{ marginTop: '20px' }}>
          {formulaciones.map((formulacion) => (
            <div
              key={formulacion.id}
              className="formula-list-item"
              onClick={() => navigate(`/system/formulaciones/${formulacion.id}`)}
            >
              <div className="formula-info">
                <span className="formula-name">{formulacion.nombreProducto}</span>
                <span className="formula-quantity">{translateContainerType(formulacion.tipoEnvase)}</span>
              </div>
              <button
                className="formula-print-btn"
                onClick={(e) => {
                  e.stopPropagation();
                  handleImprimir(formulacion);
                }}
                title="Imprimir fórmula"
              >
                <FiPrinter />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Modal de descarga de reporte */}
      <DownloadReportModal
        isOpen={showDownloadModal}
        onClose={() => setShowDownloadModal(false)}
        onDownload={handleDownloadReport}
        title="Descargar Reporte de Formulaciones"
        moduleName="formulaciones"
        isLoading={isDownloading}
      />
    </div>
  );
};

export default Formulaciones;
